/**
 * Attachment utilities for safely storing user-uploaded files in a database
 * (e.g. a MongoDB collection).
 *
 * A MongoDB document is capped at 16 MB (BSON). Embedding files as base64 grows
 * them by ~33 %, so consumers should keep `maxTotalSize` at or below ~12 MB of
 * **raw bytes** to stay comfortably under the BSON limit after encoding.
 * The defaults below reflect this guidance.
 *
 * Content-verification pipeline (defense in depth):
 * - `validateAttachments` (sync, UI): checks the browser-declared `file.type` —
 *   fast but spoofable. Suitable for immediate UX feedback.
 * - `prepareAttachmentsForStorage` (async): sniffs magic bytes from the actual
 *   file content and cross-checks with the whitelist. This **reduces the risk** of
 *   a malicious file disguised with a forged extension or MIME type, but does **not
 *   guarantee** safety and cannot replace server-side antivirus scanning.
 * - `validateStoredAttachments` (sync, backend guard): re-sniffs magic bytes from
 *   the stored base64 before writing to the database. It validates the file
 *   signature, not image readability. The real defence remains on the
 *   consumer's backend (antivirus, sandboxed processing, etc.).
 */

// #region Constants
/** MIME types accepted by default: common images (PNG, JPEG, WebP, GIF) */
export const DEFAULT_ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

/** Maximum number of attachments accepted by default (10 files). */
export const DEFAULT_MAX_FILES = 10;

/**
 * Suggested opt-in per-file size cap: 5 MB (raw bytes before base64 encoding).
 * Base64 adds ~33 %, so a 5 MB file becomes ~6.7 MB encoded.
 *
 * This value is **not applied by default**. No per-file size limit is enforced
 * unless the consumer explicitly opts in via {@link AttachmentConstraints.maxFileSize}:
 *
 * @example
 * validateAttachments(files, { maxFileSize: DEFAULT_MAX_FILE_SIZE });
 */
export const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Maximum cumulative raw size of all files by default: 10 MB.
 * Keep this value at or below ~12 MB to stay under the MongoDB BSON 16 MB
 * document limit once all attachments are base64-encoded.
 */
export const DEFAULT_MAX_TOTAL_SIZE = 10 * 1024 * 1024;

/** Maximum length of a sanitized filename. */
const MAX_FILENAME_LENGTH = 200;

/** Smallest printable ASCII code point — anything below is a control character. */
const FIRST_PRINTABLE_CHAR_CODE = 0x20;
// #endregion

// #region Types
/** Tunable constraints applied when validating attachments. */
export interface AttachmentConstraints {
  /** Maximum number of files. Defaults to {@link DEFAULT_MAX_FILES}. */
  maxFiles?: number;
  /**
   * Maximum size of a single file in bytes (raw, before base64).
   * When omitted, no per-file size limit is applied.
   * Pass {@link DEFAULT_MAX_FILE_SIZE} to restore the previous 5 MB cap.
   */
  maxFileSize?: number;
  /** Maximum cumulative raw size of all files in bytes. Defaults to {@link DEFAULT_MAX_TOTAL_SIZE}. */
  maxTotalSize?: number;
  /** Allowed MIME types. Defaults to {@link DEFAULT_ALLOWED_MIME_TYPES}. */
  allowedMimeTypes?: readonly string[];
}

/**
 * Database-storable representation of an attachment.
 * `data` is base64-encoded (without the `data:` URI prefix) so it can be embedded
 * directly in a document.
 */
export interface StoredAttachment {
  filename: string;
  contentType: string;
  /** Original byte size of the file (before base64 encoding). */
  size: number;
  /** Base64-encoded file content, without the `data:<mime>;base64,` prefix. */
  data: string;
}

/** Reason an attachment was rejected. */
export type AttachmentValidationReason =
  | 'count'
  | 'type'
  | 'size'
  | 'total-size'
  | 'empty'
  | 'read-error';

/** A single validation failure tied to a file. */
export interface AttachmentValidationError {
  filename: string;
  reason: AttachmentValidationReason;
  message: string;
}

/** Thrown by {@link prepareAttachmentsForStorage} when validation fails. */
export class AttachmentError extends Error {
  readonly errors: AttachmentValidationError[];

  constructor(errors: AttachmentValidationError[]) {
    super(errors.map((e) => e.message).join(' '));
    this.name = 'AttachmentError';
    this.errors = errors;
  }
}
// #endregion

// #region Magic-byte signatures
/**
 * Magic-byte signatures used by {@link sniffMimeType}.
 * Each entry defines the byte sequence to match and the resulting MIME type.
 * Signatures are matched on their own length only — no fixed-width slice required.
 */
const MAGIC_SIGNATURES: ReadonlyArray<{
  readonly mime: string;
  readonly match: (b: Uint8Array) => boolean;
}> = [
  {
    mime: 'image/png',
    // 89 50 4E 47 0D 0A 1A 0A
    match: (b) =>
      b.length >= 8 &&
      b[0] === 0x89 &&
      b[1] === 0x50 &&
      b[2] === 0x4e &&
      b[3] === 0x47 &&
      b[4] === 0x0d &&
      b[5] === 0x0a &&
      b[6] === 0x1a &&
      b[7] === 0x0a,
  },
  {
    mime: 'image/jpeg',
    // FF D8 FF (3 bytes; the 4th byte varies by JPEG sub-format)
    match: (b) => b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff,
  },
  {
    mime: 'image/gif',
    // GIF87a or GIF89a (6 bytes)
    match: (b) => {
      if (b.length < 6) return false;
      const isGif =
        b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38 && b[5] === 0x61;
      return isGif && (b[4] === 0x37 || b[4] === 0x39); // '7' or '9'
    },
  },
  {
    mime: 'image/webp',
    // bytes 0-3: RIFF  AND  bytes 8-11: WEBP  (requires ≥12 bytes)
    match: (b) =>
      b.length >= 12 &&
      b[0] === 0x52 &&
      b[1] === 0x49 &&
      b[2] === 0x46 &&
      b[3] === 0x46 &&
      b[8] === 0x57 &&
      b[9] === 0x45 &&
      b[10] === 0x42 &&
      b[11] === 0x50,
  },
  {
    mime: 'application/pdf',
    // %PDF- (25 50 44 46 2D — 5 bytes)
    match: (b) =>
      b.length >= 5 &&
      b[0] === 0x25 &&
      b[1] === 0x50 &&
      b[2] === 0x44 &&
      b[3] === 0x46 &&
      b[4] === 0x2d,
  },
];
// #endregion

// #region Helpers
const resolveConstraints = (constraints?: AttachmentConstraints) => ({
  maxFiles: constraints?.maxFiles ?? DEFAULT_MAX_FILES,
  /** undefined means no per-file limit; only set when the consumer opts in. */
  maxFileSize: constraints?.maxFileSize,
  maxTotalSize: constraints?.maxTotalSize ?? DEFAULT_MAX_TOTAL_SIZE,
  allowedMimeTypes: constraints?.allowedMimeTypes ?? DEFAULT_ALLOWED_MIME_TYPES,
});

/** Formats a byte count as a human-readable string (e.g. "2 MB"). */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
};

/**
 * @function sanitizeFilename
 * Produces a safe *display/storage* string from a user-supplied filename: strips any
 * directory path, drops control and filesystem-unsafe characters, rejects pure-dot
 * names (".", ".."), collapses whitespace and caps the length. Always returns a
 * non-empty name. This is not a guarantee of filesystem safety — treat the result as
 * a string field, not a path.
 */
export const sanitizeFilename = (name: string): string => {
  const base = (name ?? '').split(/[/\\]/).pop() ?? '';
  const cleaned = Array.from(base)
    .filter((char) => char.charCodeAt(0) >= FIRST_PRINTABLE_CHAR_CODE)
    .join('')
    .replace(/[<>:"|?*]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+$/, '')
    .slice(0, MAX_FILENAME_LENGTH);
  return cleaned || 'attachment';
};

/** Shared per-item type/size/empty checks used by both File and StoredAttachment validators. */
const checkItem = (
  item: { filename: string; contentType: string; size: number },
  resolved: ReturnType<typeof resolveConstraints>,
): AttachmentValidationError | null => {
  if (item.size === 0) {
    return { filename: item.filename, reason: 'empty', message: `"${item.filename}" is empty.` };
  }
  if (!resolved.allowedMimeTypes.includes(item.contentType)) {
    return {
      filename: item.filename,
      reason: 'type',
      message: `"${item.filename}" has an unsupported type (${item.contentType || 'unknown'}).`,
    };
  }
  if (resolved.maxFileSize !== undefined && item.size > resolved.maxFileSize) {
    return {
      filename: item.filename,
      reason: 'size',
      message: `"${item.filename}" exceeds the ${formatBytes(resolved.maxFileSize)} per-file limit.`,
    };
  }
  return null;
};

const checkAggregate = (
  items: { size: number }[],
  resolved: ReturnType<typeof resolveConstraints>,
): AttachmentValidationError[] => {
  const errors: AttachmentValidationError[] = [];
  if (items.length > resolved.maxFiles) {
    errors.push({
      filename: '',
      reason: 'count',
      message: `Too many files: ${items.length} provided, ${resolved.maxFiles} allowed.`,
    });
  }
  const totalSize = items.reduce((sum, item) => sum + item.size, 0);
  if (totalSize > resolved.maxTotalSize) {
    errors.push({
      filename: '',
      reason: 'total-size',
      message: `Attachments exceed the ${formatBytes(resolved.maxTotalSize)} total size limit.`,
    });
  }
  return errors;
};

/** Encodes a File to base64 (without the `data:` prefix) using FileReader. */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      // Strip the "data:<mime>;base64," prefix.
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.readAsDataURL(file);
  });

/**
 * Decodes only the leading `n` bytes of a base64 string (without the `data:` URI
 * prefix). This avoids decoding an entire potentially large file just to inspect its
 * magic bytes.
 *
 * Internally it slices to `ceil(n/3)*4` base64 characters (the minimum number of
 * base64 chars that encode at least `n` raw bytes) and decodes with `atob`.
 *
 * @internal Not exported — use {@link sniffMimeType} on the returned bytes.
 */
const base64HeaderBytes = (data: string, n = 12): Uint8Array => {
  const b64Slice = data.slice(0, Math.ceil(n / 3) * 4);
  try {
    const binary = atob(b64Slice);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i) & 0xff;
    }
    return bytes;
  } catch {
    return new Uint8Array(0);
  }
};

/**
 * Best-effort browser-only image readability check. Creates an object URL from the
 * blob and loads it with a hidden `<img>` element. Resolves `true` when the image
 * loads, `false` when it fails to decode, and `true` (skip) when:
 * - the environment has no `Image` constructor (Node / jsdom without canvas),
 * - `URL.createObjectURL` is unavailable,
 * - the optional `timeoutMs` elapses first.
 *
 * Always revokes the object URL in a finally block to prevent memory leaks.
 *
 * @internal Not exported — called only from {@link prepareAttachmentsForStorage}.
 */
const verifyImageReadable = (blob: Blob, timeoutMs = 5000): Promise<boolean> => {
  // Graceful degradation: skip in non-browser environments.
  if (typeof Image === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
    return Promise.resolve(true);
  }

  const objectUrl = URL.createObjectURL(blob);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    // Wrap in an object so ESLint's prefer-const does not complain: we mutate
    // the property, not the binding.
    const timer = { handle: undefined as ReturnType<typeof setTimeout> | undefined };

    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer.handle);
      resolve(value);
    };

    timer.handle = setTimeout(() => settle(true), timeoutMs);

    const img = new Image();
    img.onload = () => settle(true);
    img.onerror = () => settle(false);
    img.src = objectUrl;
  }).finally(() => {
    URL.revokeObjectURL(objectUrl);
  });
};
// #endregion

// #region Public API
/**
 * @function sniffMimeType
 * Detects the MIME type of a file by inspecting its leading magic bytes.
 *
 * Recognised signatures:
 * - `image/png`  — `89 50 4E 47 0D 0A 1A 0A` (8 bytes)
 * - `image/jpeg` — `FF D8 FF` (3 bytes; the 4th varies by sub-format)
 * - `image/gif`  — `GIF87a` or `GIF89a` (6 bytes)
 * - `image/webp` — bytes 0–3 = `RIFF` AND bytes 8–11 = `WEBP` (12 bytes)
 * - `application/pdf` — `%PDF-` (5 bytes)
 *
 * Each signature is matched on its own required length — a smaller buffer is
 * accepted only if a shorter signature still matches. Returns `null` for
 * unrecognised content or when `bytes` is empty.
 *
 * This check **reduces the risk** of a malicious file disguised with a forged
 * extension or MIME type, but does **not guarantee** file safety. Treat it as
 * one layer of defence in depth; server-side antivirus and sandboxed processing
 * remain the authoritative defence.
 */
export const sniffMimeType = (bytes: Uint8Array): string | null => {
  for (const sig of MAGIC_SIGNATURES) {
    if (sig.match(bytes)) return sig.mime;
  }
  return null;
};

/**
 * @function validateAttachments
 * Synchronously validates a list of `File` objects against the given constraints
 * using the browser-declared `file.type` (not the actual file content). Designed
 * for immediate UI feedback when files are selected. Returns an array of errors
 * (empty if everything is valid).
 *
 * Note: this function trusts the browser-reported MIME type which is
 * client-controlled and can be spoofed. Content verification happens later in
 * {@link prepareAttachmentsForStorage}.
 */
export const validateAttachments = (
  files: File[],
  constraints?: AttachmentConstraints,
): AttachmentValidationError[] => {
  const resolved = resolveConstraints(constraints);
  const errors = checkAggregate(files, resolved);
  for (const file of files) {
    const error = checkItem(
      { filename: file.name, contentType: file.type, size: file.size },
      resolved,
    );
    if (error) errors.push(error);
  }
  return errors;
};

/**
 * @function validateStoredAttachments
 * Validates already-encoded {@link StoredAttachment}s against the constraints.
 * Use this as a final synchronous guard inside `onSendFeedback`, before writing
 * the document to the database (defense in depth on top of the UI validation).
 *
 * In addition to the standard size/count/type checks, this function decodes the
 * leading bytes of each attachment's base64 `data` field and verifies the magic
 * byte signature against the whitelist. This **reduces the risk** of a malicious
 * file disguised with a forged content type, but does **not guarantee** safety —
 * the consumer's backend (antivirus, sandboxed processing) remains the
 * authoritative defence.
 *
 * Note: image readability is NOT verified here (browser-only). Signature
 * validation only.
 *
 * Returns an array of errors (empty if everything is valid).
 */
export const validateStoredAttachments = (
  attachments: StoredAttachment[],
  constraints?: AttachmentConstraints,
): AttachmentValidationError[] => {
  const resolved = resolveConstraints(constraints);
  const errors = checkAggregate(attachments, resolved);
  for (const attachment of attachments) {
    const itemError = checkItem(
      {
        filename: attachment.filename,
        contentType: attachment.contentType,
        size: attachment.size,
      },
      resolved,
    );
    if (itemError) {
      errors.push(itemError);
      continue; // no point sniffing if basic checks already failed
    }

    // Magic-byte verification: decode the header bytes and sniff.
    const headerBytes = base64HeaderBytes(attachment.data);
    const detected = sniffMimeType(headerBytes);
    if (detected === null || !resolved.allowedMimeTypes.includes(detected)) {
      errors.push({
        filename: attachment.filename,
        reason: 'type',
        message: `"${attachment.filename}" content does not match an allowed type.`,
      });
    }
  }
  return errors;
};

/**
 * @function prepareAttachmentsForStorage
 * Validates then encodes `File` objects into database-storable {@link StoredAttachment}s
 * (base64-embedded). Throws an {@link AttachmentError} carrying the validation errors
 * if any constraint is violated, so the caller can surface them.
 *
 * After encoding each file the function:
 * 1. Sniffs magic bytes from the base64 header to detect the actual content type.
 *    This **reduces the risk** of a malicious file disguised as an allowed type, but
 *    does **not guarantee** safety — server-side antivirus remains essential.
 * 2. If the detected type is an image, attempts to decode it via `<img>` in the
 *    browser to catch corrupt or partially-valid files.
 * 3. Stores the **detected** MIME type as `contentType` (the browser-declared
 *    `file.type` is discarded in favour of the sniffed value).
 */
export const prepareAttachmentsForStorage = async (
  files: File[],
  constraints?: AttachmentConstraints,
): Promise<StoredAttachment[]> => {
  const resolved = resolveConstraints(constraints);
  const initialErrors = validateAttachments(files, constraints);
  if (initialErrors.length > 0) throw new AttachmentError(initialErrors);

  const attachments: StoredAttachment[] = [];
  const contentErrors: AttachmentValidationError[] = [];

  for (const file of files) {
    let data: string;
    try {
      data = await fileToBase64(file);
    } catch {
      throw new AttachmentError([
        { filename: file.name, reason: 'read-error', message: `Failed to read "${file.name}".` },
      ]);
    }

    // Sniff actual content type from magic bytes.
    const detected = sniffMimeType(base64HeaderBytes(data));

    if (detected === null || !resolved.allowedMimeTypes.includes(detected)) {
      contentErrors.push({
        filename: file.name,
        reason: 'type',
        message: `"${file.name}" content does not match an allowed type.`,
      });
      continue;
    }

    // For images, verify the browser can actually decode them.
    if (detected.startsWith('image/')) {
      const readable = await verifyImageReadable(file);
      if (!readable) {
        contentErrors.push({
          filename: file.name,
          reason: 'type',
          message: `"${file.name}" could not be decoded as an image.`,
        });
        continue;
      }
    }

    attachments.push({
      filename: sanitizeFilename(file.name),
      contentType: detected,
      size: file.size,
      data,
    });
  }

  if (contentErrors.length > 0) throw new AttachmentError(contentErrors);

  return attachments;
};
// #endregion
